import {
    Box, Grid, Tab, Tabs, Typography
} from '@mui/material';
import { ActionButton, CancelButtonGrey } from '@tassat/button';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { CustomerModifyUserReq } from '@tassat/fred-definitions';
import { AdminUserRecordMapped } from '@tassat/fred-extended-definitions';
import {
    ClientUser as ClientUserIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Lock as LockIcon,
    Phone as PhoneIcon,
    SsoUser as SSOUserIcon,
    Unlock as UnlockIcon
} from '@tassat/icon';
import { TabPanel } from '@tassat/layout';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import history from 'Lib/browserHistory';
import { LOCK_ICON_COLOR } from 'Lib/constant';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getUser } from 'Selectors/common';
import { getAdminUserMapped } from 'Selectors/users';
import UserActivityLogGrid from 'SharedComponents/users/userActivityLogGrid';
import UserDetails from 'SharedComponents/users/userDetails';
import UserStatistics from 'SharedComponents/users/userStatistics';

import type { AdminUserDetailsProps } from '.';

export function AdminUserDetails (props: AdminUserDetailsProps) {
    const {
        classes,
        editAdminUser,
        filterModel,
        sortModel,
        isSSOEnabled
    } = props;

    const currentUser = useAppSelector(getUser);
    const adminUser = useAppSelector((state) => getAdminUserMapped(state, props.match.params.userId, false, '', ''));
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);

    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);
    const [ currentTab, setCurrentTab ] = React.useState<number>(0);

    React.useEffect(() => {
        if (!adminUser || (adminUser && (Object.keys(adminUser).length === 0 || adminUser.IsDeleted))) {
            history.push('/admin/users');
        }
    }, [adminUser]);

    const deleteAdminUserValidation = (user: AdminUserRecordMapped): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: user,
                actionType: ActionTypes.delete,
                confirmTitle: `Confirm Admin User ${user.Email} will be DELETED`,
                confirmBody: `Please confirm Admin user: ${user.Email} is being DELETED.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const deleteAdminUser = async (user: AdminUserRecordMapped) => {
        try {
            const sendValues = { ...user };

            const response = await Worker.getInstance()
                .deleteUser(sendValues);
            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Admin User ${user.Email} Successfully Deleted`,
                        successBody: `Admin User ${user.Email} has been successfully deleted.`
                    }
                };

                //No need to close alert and form modal as we are redirecting
                history.push('/admin/users', {
                    filterModel,
                    sortModel
                });
            } else if (response.data && response.data.Status === 49) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete ${user.Email}`,
                        failureBody: `Could not delete Admin user ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            } else if (response.data && response.data.Status === 10) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete ${user.Email}`,
                        failureBody: `${user.Email} cannot be deleted because they are the last Help Desk Admin with ${response.data?.CurCode ? `the ${response.data?.CurCode}` : 'a currency'} permission`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            } else if (response.data && response.data.Status === 93) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: 'Failed to Delete User',
                        failureBody: `${user.Email} has pending transactions and cannot be deleted.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete ${user.Email}`,
                        failureBody: `Could not delete Admin user ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Delete ${user.Email}`,
                    failureBody: `Something went wrong deleting Admin user ${user.Email}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const lockAdminUserValidation = (adminUser: AdminUserRecordMapped): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: adminUser,
                actionType: adminUser.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${adminUser.IsLocked ? 'Unlocking' : 'Locking'} User ${adminUser.Email}`,
                confirmBody: `Please confirm ${adminUser.IsLocked ? 'unlocking' : 'locking'} user functionality for ${adminUser.Email}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockAdminUser = async (adminUser: AdminUserRecordMapped) => {
        try {
            // User ID and Email needed for all User updates
            const userUpdateValues: Partial<CustomerModifyUserReq> = {
                UserId: adminUser.UserId,
                Email: adminUser.Email,
                IsLocked: !adminUser.IsLocked
            };

            const response = await Worker.getInstance()
                .updateUser('-1', userUpdateValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${adminUser.IsLocked ? 'Unlocked' : 'Locked'} ${adminUser.Email}`,
                        successBody: `Successfully ${adminUser.IsLocked ? 'unlocked' : 'locked'} user functionality for ${adminUser.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${adminUser.IsLocked ? 'Unlock' : 'Lock'} ${adminUser.Email}`,
                        failureBody: `Could not ${adminUser.IsLocked ? 'unlock' : 'lock'} user functionality for ${adminUser.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${adminUser.IsLocked ? 'Unlock' : 'Lock'} ${adminUser.Email}`,
                    failureBody: `Something went wrong ${adminUser.IsLocked ? 'unlocking' : 'locking'} user functionality for ${adminUser.Email}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: adminUser,
                setSelectedItem: () => adminUser,
                preActionValidationAndConfig: {
                    [ActionTypes.lock]: lockAdminUserValidation,
                    [ActionTypes.unlock]: lockAdminUserValidation,
                    [ActionTypes.delete]: deleteAdminUserValidation
                },
                onConfirm: {
                    [ActionTypes.lock]: lockAdminUser,
                    [ActionTypes.unlock]: lockAdminUser,
                    [ActionTypes.delete]: deleteAdminUser
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='column'
                justifyContent='center'
                spacing={0}
            >
                <Grid
                    className={classes.cancelButton}
                    item
                >
                    <CancelButtonGrey
                        onClick={() => history.push('/admin/users', {
                            filterModel,
                            sortModel
                        })}
                    />
                </Grid>
                <Grid
                    className={classes.userContainer}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.userHeaderContainer}
                        container
                        direction='row'
                        spacing={0}
                    >
                        <Grid
                            item
                            xs={10}
                        >
                            <Grid
                                container
                                direction='row'
                                spacing={0}
                                wrap='nowrap'
                            >
                                <Grid item>
                                    <Grid
                                        className={classes.userFullNameContainer}
                                        container
                                        direction='row'
                                        spacing={0}
                                        wrap='nowrap'
                                    >
                                        <Typography
                                            className={classes.userFullName}
                                            id='adminUserDetailsUserFullNameValue'
                                            variant='h4'
                                        >
                                            <strong>{`${adminUser.FirstName} ${adminUser.LastName}`}</strong>
                                        </Typography>
                                        {adminUser.IsLocked &&
                                            <Grid
                                                className={classes.userLockedIcon}
                                                item
                                            >
                                                <LockIcon
                                                    iconColor={LOCK_ICON_COLOR}
                                                    id='adminUserDetailsUserLockedStatus'
                                                />
                                            </Grid>}
                                    </Grid>
                                    <Box mt={1} />
                                    {
                                        adminUser.Email &&
                                        <Grid
                                            container
                                            item
                                            spacing={1}
                                        >
                                            <Grid item>
                                                <EmailIcon id='adminUserDetailsEmail' />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant='body2'>
                                                    {adminUser.Email}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    }
                                    {
                                        adminUser.Username &&
                                        <Grid
                                            container
                                            item
                                            spacing={1}
                                        >
                                            <Grid item>
                                                {
                                                    isSSOEnabled ?
                                                        <SSOUserIcon id='adminUserDetailsSSOUserID' />
                                                        : <ClientUserIcon id='adminUserDetailsUserId' />
                                                }
                                            </Grid>
                                            <Grid item>
                                                <Typography variant='body2'>
                                                    {adminUser.Username}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    }
                                    {
                                        adminUser.Phone &&
                                        <Grid
                                            container
                                            item
                                            spacing={1}
                                        >
                                            <Grid item>
                                                <PhoneIcon id='adminUserDetailsPhone' />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant='body2'>
                                                    {adminUser.Phone}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                        {currentUser.UserId !== adminUser.UserId &&
                            <Grid
                                container
                                direction='row'
                                item
                                spacing={0}
                                wrap='nowrap'
                                xs={2}
                            >
                                <ActionButton
                                    className={classes.actionButton}
                                    handleClick={() => lockAdminUserValidation(adminUser)}
                                    Icon={adminUser.IsLocked ? UnlockIcon : LockIcon}
                                    id='adminUserDetailsLockUserButton'
                                    text={adminUser.IsLocked ? 'Unlock' : 'Lock'}
                                    type='icon'
                                />
                                <ActionButton
                                    className={classes.actionButton}
                                    handleClick={() => editAdminUser(adminUser, () => deleteAdminUserValidation)}
                                    Icon={EditIcon}
                                    id='adminUserDetailsEditUserButton'
                                    text='Edit'
                                    type='icon'
                                />
                            </Grid>}
                    </Grid>
                    <Tabs
                        classes={{ indicator: classes.indicator }}
                        className={classes.userTabsContainer}
                        onChange={(e, newTab) => setCurrentTab(newTab)}
                        value={currentTab}
                        variant='fullWidth'
                    >
                        <Tab
                            className={classes.userTab}
                            id='adminUserDetailsUserDetailsTab'
                            label='Details'
                        />
                        <Tab
                            className={classes.userTab}
                            id='adminUserDetailsUserStatisticsTab'
                            label='User Statistics'
                        />
                        <Tab
                            className={classes.userTab}
                            id='adminUserDetailsUserActivityTab'
                            label='Activity Log'
                        />
                    </Tabs>
                    <Grid
                        item
                        xs={12}
                    >
                        <TabPanel
                            index={0}
                            value={currentTab}
                        >
                            <UserDetails
                                currentUser={currentUser}
                                user={adminUser}
                            />
                        </TabPanel>
                        <TabPanel
                            index={1}
                            value={currentTab}
                        >
                            <UserStatistics
                                user={adminUser}
                            />
                        </TabPanel>
                        <TabPanel
                            index={2}
                            value={currentTab}
                        >
                            <UserActivityLogGrid
                                user={adminUser}
                            />
                        </TabPanel>
                    </Grid>
                </Grid>
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}